import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IconBell, IconCheck } from "@tabler/icons-react";
import {
  useNotificationsQuery,
  useUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  type Notification,
} from "../../api/queries/notificationQueries";
import { formatRelativeTimeFromApi } from "../../utils/date";

const NotificationItem = ({
  notification,
  onNotificationClick,
}: {
  notification: Notification;
  onNotificationClick: (notification: Notification) => void;
}) => {
  return (
    <button
      onClick={() => onNotificationClick(notification)}
      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
        !notification.is_read ? "bg-blue-50/50" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
            notification.is_read ? "bg-transparent" : "bg-blue-500"
          }`}
        />
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm ${
              notification.is_read
                ? "text-gray-700"
                : "text-gray-900 font-semibold"
            }`}
          >
            {notification.title}
          </p>
          <p className="text-sm text-gray-600 mt-0.5 truncate">
            {notification.body}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {formatRelativeTimeFromApi(notification.created_at)}
          </p>
        </div>
      </div>
    </button>
  );
};

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadCountData } = useUnreadCountQuery();
  const {
    data: notifications,
    isLoading,
    refetch,
  } = useNotificationsQuery(isOpen);
  const markReadMutation = useMarkNotificationReadMutation();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();

  const unreadCount = unreadCountData?.count ?? 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleBellClick = () => {
    if (!isOpen) {
      refetch();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }

    // Navigate to the related entity using data
    if (notification.type === "customer_note_mention") {
      const customerId = notification.data?.customer_organization_id;
      const noteId = notification.data?.note_id;
      const paymentExternalId = notification.data?.payment_external_id;
      const query = noteId ? `?noteId=${noteId}` : "";
      if (paymentExternalId) {
        navigate(`/invoice/${paymentExternalId}${query}`);
      } else if (customerId) {
        navigate(`/customers/${customerId}${query}`);
      }
    } else if (notification.data?.payment_external_id) {
      navigate(`/invoice/${notification.data.payment_external_id}`);
    }

    setIsOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-md hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <IconBell size={20} stroke={1.5} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isPending}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconCheck size={16} />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Loading...
              </div>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onNotificationClick={handleNotificationClick}
                />
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                No notifications
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
